import { useState, useEffect } from "react";

function getLocalValue<T>(key: string, initValue: T | (() => T)): T {
  if (typeof window === "undefined") {
    return typeof initValue === "function" ? (initValue as () => T)() : initValue;
  }

  const rawValue = localStorage.getItem(key);
  const localValue = rawValue ? JSON.parse(rawValue) : null;

  if (localValue !== null) {
    return localValue;
  }

  const defaultValue = typeof initValue === "function" ? (initValue as () => T)() : initValue;
  return defaultValue;
}

function useU2000LocalStorage<T>(key: string, initValue: T | (() => T)): [T, (newValue: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    return getLocalValue<T>(key, initValue);
  });

  // If the storage key changes (e.g., user or organization changed), reload
  // the value from localStorage so the hook reflects the correct entry.
  // Only re-run when `key` changes and only update state if the stored value
  // is different to avoid render loops when `initValue` is recreated each render.
  useEffect(() => {
    try {
      const newVal = getLocalValue<T>(key, initValue);
      setValue((prev) => {
        try {
          if (JSON.stringify(prev) === JSON.stringify(newVal)) return prev;
        } catch (_) {
          // If stringify fails for some reason, fall back to replacing
        }
        return newVal;
      });
    } catch (err) {
      // ignore read errors
    }
    // Intentionally only depend on `key` to avoid loops caused by initValue being recreated.
  }, [key]);

  const customSetValue = (newValue: T | ((prev: T) => T)) => {
    setValue((prevValue) => {
      const valueToStore = typeof newValue === "function" ? (newValue as (prev: T) => T)(prevValue) : newValue;
      try {
        localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
      return valueToStore;
    });
  };

  return [value, customSetValue];
}

export default useU2000LocalStorage;
